import { ElementRef, Injectable, OnDestroy } from '@angular/core';
import { IResolve } from '@df/api/request/request.service';
import { App } from '@df/core/app';
import { TimeoutService } from '@df/ng/timeout.service';
import { ResizeService } from '@df/ui/common/resize.service';
import { initialPhotoswipeOptions, initialPhotoswipeUiOptions } from '@df/ui/photoswipe/photoswipe.options';
import { Photoswipe, PhotoswipeItem, PhotoswipeOptions, PhotoswipeUiDefaultOptions } from '@df/ui/photoswipe/photoswipe.types';
import { requestIdleCallback } from '@df/ui/quicklink/utils/idle';
import orderBy from 'lodash-es/orderBy';
import { BehaviorSubject, Observable, Subject, fromEvent } from 'rxjs';
import { map, mergeMap, takeUntil } from 'rxjs/operators';

export const PHOTOSWIPE_CACHE_NAME = 'photoswipe';

@Injectable({
    providedIn: 'root'
})
export class PhotoswipeService implements OnDestroy {
    protected ngUnsubscribe$ = new Subject<void>();
    readonly state$ = new BehaviorSubject<boolean>(false);
    readonly changed$ = new Subject<number>();
    protected zoomEnter$ = new Subject<void>();
    protected zoomLeave$ = new Subject<void>();
    protected zoomMove$!: Observable<Event>;

    /** @internal */
    private _opened?: boolean;

    photoswipeElementRef?: ElementRef;
    photoswipe!: Photoswipe<PhotoswipeOptions>;
    options!: Partial<PhotoswipeOptions>;

    constructor(
        protected app: App,
        private timeoutService: TimeoutService,
        protected resizeService: ResizeService
    ) {
        this.app.client.obs.breakpoint.pipe(takeUntil(this.ngUnsubscribe$)).subscribe(() => {
            // sets a flag that slides should be updated
            this.photoswipe?.invalidateCurrItems();
            // updates the content of slides
            this.photoswipe?.updateSize(true);
        });

        if (this.app.isPlatformBrowser) {
            window.addEventListener('popstate', event => this.handlePopState(event), false);
        }
    }

    ngOnDestroy(): void {
        this.ngUnsubscribe$.next();
        this.ngUnsubscribe$.complete();

        if (this.app.isPlatformBrowser) {
            window.removeEventListener('popstate', event => this.handlePopState(event), false);
        }
    }

    canDeactivate() {
        if (this.app.isPlatformBrowser) {
            if (this._opened || this.state$.value) {
                this.photoswipe?.close();

                return false;
            } else if (history.state?.photoswipeOpen && history.state?.initialIndex !== undefined) {
                this.open(this.options, undefined, history.state.initialIndex);

                return false;
            }
        }

        return true;
    }

    getItems(galleryUID = this.options.galleryUID): PhotoswipeItem[] {
        const items = this.app.cache.get([PHOTOSWIPE_CACHE_NAME, galleryUID]) || [];

        return orderBy<PhotoswipeItem>(items, ['index']).filter(item => !!item);
    }

    async open(options: Partial<PhotoswipeOptions>, item: PhotoswipeItem | undefined, initialIndex?: number): Promise<void> {
        if (this.app.isPlatformBrowser && (item || initialIndex !== undefined)) {
            if (initialIndex === undefined && item) {
                initialIndex = this.index(item, options.galleryUID);
            }

            options.index = initialIndex;

            const _options = this.createOptions(options);

            const initialItems = this.getItems(options.galleryUID);
            const onDestroy = () => {
                // reset initial zoom level for all images
                initialItems.forEach(_item => {
                    const itemNew = {
                        ..._item,
                        zoomLevel: undefined
                    };

                    this.update(itemNew, options.galleryUID);
                });

                this._opened = false;
            };

            if (this.options.closeOnHistoryBack && !history.state?.photoswipeOpen) {
                try {
                    history.pushState({ photoswipeOpen: true, initialIndex }, '');
                } catch (error) {
                    this.app.logger.error(error);
                }
            }

            const { default: photoSwipe } = await import('./src/photoswipe');
            const { default: photoSwipeUI } = await import('./src/photoswipe-ui-default');

            this.photoswipe = new photoSwipe(
                this.photoswipeElementRef!.nativeElement,
                photoSwipeUI,
                initialItems,
                _options
            ) as PhotoSwipe<PhotoswipeOptions>;

            this.photoswipe.listen('close', () => {
                onDestroy();
            });

            this.photoswipe.listen('destroy', () => {
                this.photoswipe.invalidateCurrItems();
                this.photoswipe.updateSize(true);
                this.state$.next(false);
            });

            this.photoswipe.listen('beforeChange', () => {
                if (!this._opened) {
                    return;
                }

                // reset initial zoom level
                const _item = this.photoswipe.currItem as PhotoswipeItem;

                if (_item) {
                    _item.zoomLevel = undefined;
                }
            });

            this.photoswipe.listen('afterChange', () => {
                if (!this._opened) {
                    return;
                }

                this.resetItem();

                if (this.state$.value) {
                    this._zoomTo();
                }

                const container = (<any>this.photoswipe.currItem).container;

                if (container) {
                    container.parentElement.style.display = 'block';
                }

                const index = this.photoswipe.getCurrentIndex();
                this.changed$.next(index);
            });

            this.photoswipe.listen('initialZoomIn', () => {
                this._opened = true;

                try {
                    const currentItem = this.photoswipe.currItem;
                    const currentItems = this.photoswipe.items as PhotoswipeItem[];
                    if (
                        currentItems &&
                        typeof initialIndex !== 'undefined' &&
                        currentItems.length &&
                        currentItems[initialIndex] &&
                        currentItems[initialIndex] !== currentItem
                    ) {
                        if (this.photoswipe.currItem.bounds) {
                            this.photoswipe.goTo(initialIndex);
                        }

                        this._zoomTo();
                    }
                } catch (error) {
                    this.app.logger.error(error);
                }
            });

            this.photoswipe.listen('initialZoomInEnd', () => {
                try {
                    const currentItem = this.photoswipe.currItem;
                    const currentItems = this.photoswipe.items;

                    if (
                        currentItems &&
                        typeof initialIndex !== 'undefined' &&
                        currentItems.length &&
                        currentItems[initialIndex] &&
                        currentItems[initialIndex] !== currentItem
                    ) {
                        if (this.photoswipe.currItem.bounds) {
                            this.photoswipe.goTo(initialIndex);
                        }

                        this._zoomTo();
                    }

                    let container = (<any>this.photoswipe.currItem).container;

                    if (container) {
                        container.parentElement.style.display = 'block';
                    } else {
                        this.timeoutService.setTimeout(() => {
                            this.photoswipe.updateSize(true);
                            container = (<any>this.photoswipe.currItem).container;

                            if (container) {
                                container.parentElement.style.display = 'block';
                            }
                        }, 1000);
                    }
                } catch (error) {
                    this.app.logger.error(error);
                }
            });

            this.photoswipe.listen('initialZoomOut', () => {
                this._opened = false;
            });

            if (_options.initialZoom && _options.initialZoom !== 1) {
                this.photoswipe.listen('initialZoomInEnd', () => {
                    const destZoomLevel = (this.photoswipe?.currItem?.fitRatio || 1) * (_options?.initialZoom || 1);

                    this._zoomTo(destZoomLevel);
                });
            }

            if (_options.followMouse && !this.app.client.isTouchy && this.app.isPlatformBrowser) {
                this.zoomMove$ = fromEvent(document, 'mousemove');

                this.photoswipe.listen('initialZoomInEnd', () => {
                    this.zoomEnter$.next();
                });

                this.photoswipe.listen('initialZoomOut', () => {
                    this.zoomLeave$.next();
                });

                this.photoswipe.listen('beforeChange', () => {
                    this.zoomLeave$.next();
                });

                this.photoswipe.listen('afterChange', () => {
                    this.zoomEnter$.next();
                });

                this.zoomEnter$
                    .pipe(
                        // eslint-disable-next-line @typescript-eslint/no-unused-vars
                        mergeMap(_ =>
                            this.zoomMove$.pipe(
                                map((e: Event) => e),
                                // complete inner observable on mouseleave event
                                takeUntil(this.zoomLeave$)
                            )
                        ),
                        takeUntil(this.ngUnsubscribe$)
                    )
                    .subscribe(e => this.mouseMove(e));
            }

            this.photoswipe.init();

            this.state$.next(true);
        }
    }

    private _zoomTo(destZoomLevel = this.photoswipe.currItem.fitRatio, speed = this.options.showAnimationDuration) {
        this.photoswipe.zoomTo(
            Math.min(destZoomLevel || 1, 1),
            {
                x: this.photoswipe.viewportSize.x / 2,
                y: this.photoswipe.viewportSize.y / 2
            },
            speed || 0
        );
    }

    updateSize(): void {
        this.photoswipe.updateSize(true);
    }

    fetchAll(galleryUID = this.options.galleryUID): Promise<void> {
        return new Promise((resolve, reject) => {
            const promises: Promise<any>[] = [];

            // preload  images
            this.getItems(galleryUID).forEach(item => {
                if (!item.fetch && !item.html && item.source) {
                    const isExternal = !!item.source.match(/^https?:\/\//);
                    const width = this.options?.originalZoom ? item!.w! * this.options.originalZoom : 0;
                    const height = this.options?.originalZoom ? item!.h! * this.options.originalZoom : 0;
                    // original size image
                    const url =
                        isExternal || item.source.match(/\.(svg|gif)$/) || (!width && !height)
                            ? this.app.url.toMediaUrl(item.source)
                            : this.resizeService.resize(this.app.url.toMediaUrl(item.source), {
                                  width: this.options?.originalZoom ? item!.w! * this.options.originalZoom : 0,
                                  height: this.options?.originalZoom ? item!.h! * this.options.originalZoom : 0,
                                  ...this.options?.imageOptions
                              });
                    const promise = this.fetchAndUpdate({ src: url, uid: item.uid }, galleryUID, true, isExternal);
                    if (promise) {
                        promises.push(promise);
                    }
                }
            });

            return Promise.all(promises)
                .then(() => resolve())
                .catch(() => reject());
        });
    }

    mouseMove(e): void {
        const item = this.photoswipe.currItem as PhotoswipeItem;

        if (!item.zoomLevel || item.zoomLevel === item.initialZoomLevel) {
            return;
        }

        const viewportSize = this.photoswipe.viewportSize;
        const bounds = item.bounds;

        const x = e.clientX / viewportSize.x;
        const y = e.clientY / viewportSize.y;
        const left = bounds.max.x * x;
        const top = bounds.max.y * y;

        (<any>this.photoswipe).panTo(left, top);
    }

    add(item: PhotoswipeItem, galleryUID = this.options.galleryUID, index = -1): PhotoswipeItem {
        const exists = this.exists(item, galleryUID);

        if (!exists) {
            this.push(item, galleryUID, index);
        } else if (!(<PhotoswipeItem>exists)?.fetch) {
            this.update(item, galleryUID);
        }

        return <PhotoswipeItem>exists || item;
    }

    push(item: PhotoswipeItem, galleryUID = this.options.galleryUID, index: number): void {
        let cachedItems = this.getItems(galleryUID);

        if (!cachedItems) {
            cachedItems = [];
        }

        if (index !== undefined && typeof index === 'number') {
            cachedItems.splice(index, 0, item);
        } else {
            cachedItems.push(item);
        }

        this.app.cache.set([PHOTOSWIPE_CACHE_NAME, galleryUID], cachedItems);
    }

    update(item: PhotoswipeItem, galleryUID = this.options.galleryUID): PhotoswipeItem[] {
        const cachedItems = this.getItems(galleryUID);
        const currentIndex = cachedItems ? cachedItems.findIndex((m: PhotoswipeItem) => m.uid === item.uid) : -1;

        if (currentIndex !== -1) {
            const currentItem = cachedItems[currentIndex];

            cachedItems[currentIndex] = Object.assign(currentItem, item);

            this.app.cache.set([PHOTOSWIPE_CACHE_NAME, galleryUID], cachedItems);
        }

        return this.getItems(galleryUID);
    }

    moveItem(item: PhotoswipeItem, uid: string, galleryUID = this.options.galleryUID, oldGalleryUID: number): PhotoswipeItem[] {
        const items = this.getItems(galleryUID);
        const oldItems = this.getItems(oldGalleryUID);
        const index = oldItems ? oldItems.findIndex((m: PhotoswipeItem) => m.uid === item.uid) : -1;

        if (index !== -1) {
            const currentItem = { ...oldItems[index] };

            currentItem.uid = uid;

            // remove from old gallery
            oldItems.splice(index, 1);

            this.app.cache.set([PHOTOSWIPE_CACHE_NAME, oldGalleryUID], oldItems);

            if (!this.exists(currentItem, galleryUID)) {
                items.push(currentItem);
            }
        } else if (!this.exists(item, galleryUID)) {
            items.push(item);
        }

        this.app.cache.set([PHOTOSWIPE_CACHE_NAME, galleryUID], items);

        return items;
    }

    exists(item: PhotoswipeItem, galleryUID = this.options.galleryUID): undefined | PhotoswipeItem {
        const cachedItems: PhotoswipeItem[] = this.getItems(galleryUID);

        return cachedItems ? cachedItems.find((m: PhotoswipeItem) => m?.uid === item?.uid) : undefined;
    }

    index(item: PhotoswipeItem, galleryUID = this.options.galleryUID, items?: PhotoswipeItem[]): number {
        const _items: PhotoswipeItem[] = items || this.getItems(galleryUID);

        return _items ? _items.findIndex((m: PhotoswipeItem) => m?.uid && m.uid === item.uid) : -1;
    }

    /**
     * Load images in background.
     */
    fetchAndUpdate(
        item: PhotoswipeItem,
        galleryUID = this.options.galleryUID,
        force = false,
        preventFetch = false
    ): Promise<PhotoswipeItem[]> {
        return new Promise((resolve: IResolve) => {
            if (item.loaded || item.loading || item.fetch || item.html) {
                return resolve(this.getItems(galleryUID));
            }

            const update = (data: PhotoswipeItem) => {
                item.loading = false;

                const items = this.update({ ...item, ...data }, galleryUID);

                return resolve(items);
            };

            const src = item.src;

            if (src) {
                if (preventFetch) {
                    dimensions(src, update);
                } else if (force) {
                    item.loading = true;

                    fetch(src)
                        .catch(error => {
                            this.app.logger.error(error);
                        })
                        .finally(() => {
                            dimensions(src, update);
                        });
                } else {
                    requestIdleCallback(() => {
                        item.loading = true;

                        fetch(src)
                            .catch(error => {
                                this.app.logger.error(error);
                            })
                            .finally(() => {
                                dimensions(src, update);
                            });
                    });
                }
            }
        });
    }

    updateOptions(options: Partial<PhotoswipeOptions>): void {
        this.options = {
            ...this.options,
            ...options
        };
    }

    protected createOptions(options: Partial<PhotoswipeOptions>): Partial<PhotoswipeOptions> {
        const defaults: Partial<PhotoswipeOptions> = initialPhotoswipeOptions;
        const defaultsUi: Partial<PhotoswipeUiDefaultOptions> = initialPhotoswipeUiOptions(this.photoswipe);

        return {
            ...defaults,
            ...defaultsUi,
            ...this.options,
            ...options
        };
    }

    resetItem() {
        const _item = this.photoswipe.currItem as PhotoswipeItem;

        if (_item) {
            _item.zoomLevel = _item.initialZoomLevel;
        }

        this._zoomTo(this.photoswipe.currItem.fitRatio, 0);
    }

    protected handlePopState(event: PopStateEvent): void {
        if (this.app.isPlatformBrowser) {
            if (event.state?.photoswipeOpen && this.options.closeOnHistoryBack) {
                if (this._opened) {
                    this.photoswipe.close();
                } else if (event.state?.initialIndex !== undefined) {
                    this.open(this.options || {}, event.state.initialIndex);
                } else {
                    history.back();
                }
            } else if (this._opened) {
                this.photoswipe!.close();
            }
        }
    }
}

const dimensions = (src: string, cb: (data: PhotoswipeItem) => void) => {
    const image = new Image();
    let h = 0;
    let w = 0;

    image.onload = () => {
        h = image.naturalHeight;
        w = image.naturalWidth;

        return cb({ src, h, w, fetch: true });
    };

    image.src = src;
};
